/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import BenchlingConfig from 'BenchlingConfig';
import ExportModal from 'exports/ExportModal';
import * as HelpModals from 'help/Modals';
import InstitutionSelector from 'institutions/InstitutionSelector';
import Avatar, {hasDefaultAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HelpIcon from 'ui-library/icons/HelpIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as nyc from 'ui-library/NYC';
import * as modals from 'util/Modals';

import {Organization} from './OrganizationConstants';
import TombstoneOrganizationModal from './TombstoneOrganizationModal';

interface Props {
  isLoading: boolean;
  isCurrentUserAdmin: boolean;
  organization: Organization;
}

type State = any;

class OrganizationSettingsTab extends React.Component<Props, State> {
  override state: State = {
    name: this.props.organization.name,
    email: this.props.organization.email,
    location: this.props.organization.location || '',
    website: this.props.organization.website || '',
    institution: this.props.organization.institution,
    disablePublic: this.props.organization.disablePublic,
  };

  _handleSaveOrganizationChanges = (e: any): void => {
    e.preventDefault();
    const organizationState = {
      ...this.state,
    };
    if (organizationState.institution) {
      // Pass institution api_identifier to OrganizationDAO.
      organizationState.institution = organizationState.institution.id;
    }
    OrganizationActions.update(this.props.organization.id, organizationState);
  };

  _updateCurrentOrganization = (organizationAttribute: any): void => {
    this.setState(organizationAttribute);
  };

  _handleAvatarChange = (e: any): void => {
    if (e.target.files.length === 0) {
      return;
    }
    OrganizationActions.updateOrganizationAvatar(this.props.organization.id, e.target.files[0]);
  };

  _handleRemoveAvatar = (e: any): void => {
    OrganizationActions.removeOrganizationAvatar(this.props.organization.id);
  };

  _handleTombstone = async (): Promise<void> => {
    const confirmed = await legacyRunModal(TombstoneOrganizationModal, {
      organization: this.props.organization,
    });
    if (confirmed) {
      OrganizationActions.tombstoneOrganization(this.props.organization.id);
    }
  };

  _handleDelete = async (): Promise<void> => {
    const confirmed = await modals.confirm(
      'Delete Organization',
      `Clicking OK will delete ${this.props.organization.name} permanently. Please remove all members from
        the organization and transfer ownership of all projects before deleting the organization.`,
      'danger',
      {promiseCompatible: true}
    );
    if (confirmed) {
      OrganizationActions.deleteOrganization(this.props.organization.id);
    }
  };

  override render(): JSX.Element {
    const disableInputs = this.props.isLoading || !this.props.isCurrentUserAdmin;

    return (
      <nyc.Container size="md" dataTestAttrs={{component: 'OrganizationSettingsTab', element: 'root'}}>
        {!this.props.isCurrentUserAdmin && (
          <p>Please contact an organization admin to update the settings.</p>
        )}
        <nyc.Row>
          <nyc.Col md={7}>
            <form onSubmit={this._handleSaveOrganizationChanges}>
              <FormGroup>
                <Label>Organization Handle</Label>
                <input className="form-control" value={this.props.organization.handle} readOnly={true} />
              </FormGroup>
              <FormGroup>
                <Label>Organization Name</Label>
                <input
                  className="form-control"
                  value={this.state.name}
                  onChange={(e) => {
                    this._updateCurrentOrganization({name: e.target.value});
                  }}
                  disabled={disableInputs}
                  {...getTestProps({
                    component: 'OrganizationSettingsTab',
                    element: 'nameInput',
                  })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Organization Email</Label>
                <input
                  className="form-control"
                  value={this.state.email}
                  onChange={(e) => {
                    this._updateCurrentOrganization({email: e.target.value});
                  }}
                  disabled={disableInputs}
                  {...getTestProps({
                    component: 'OrganizationSettingsTab',
                    element: 'emailInput',
                  })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Location</Label>
                <input
                  className="form-control"
                  value={this.state.location}
                  onChange={(e) => {
                    this._updateCurrentOrganization({location: e.target.value});
                  }}
                  disabled={disableInputs}
                  {...getTestProps({
                    component: 'OrganizationSettingsTab',
                    element: 'locationInput',
                  })}
                />
              </FormGroup>
              <FormGroup>
                <Label>URL</Label>
                <input
                  className="form-control"
                  value={this.state.website}
                  onChange={(e) => {
                    this._updateCurrentOrganization({website: e.target.value});
                  }}
                  disabled={disableInputs}
                  {...getTestProps({
                    component: 'OrganizationSettingsTab',
                    element: 'websiteInput',
                  })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Institution</Label>
                <InstitutionSelector
                  allowUnverified={false}
                  disabled={disableInputs}
                  onChange={(institution) => {
                    this._updateCurrentOrganization({institution});
                  }}
                  placeholder="Select an institution or university"
                  value={this.state.institution}
                />
              </FormGroup>
              {BenchlingConfig.ENABLE_PUBLIC_FOLDERS && (
                <FormGroup>
                  <Label>
                    <input
                      type="checkbox"
                      checked={this.state.disablePublic}
                      onChange={(e) => {
                        this._updateCurrentOrganization({disablePublic: e.target.checked});
                      }}
                      disabled={disableInputs}
                      {...getTestProps({
                        component: 'OrganizationSettingsTab',
                        element: 'publicProjectsCheckbox',
                      })}
                    />
                    Disable public projects for all members
                    <HelpIcon className="u-mini-spacing-left" onClick={HelpModals.showPublicHelp} />
                  </Label>
                </FormGroup>
              )}
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                disabled={disableInputs}
                size={ButtonSize.MEDIUM}
                text={this.props.isLoading ? 'Loading...' : 'Save Changes'}
                type="submit"
                dataTestAttrs={{component: 'OrganizationSettingsTab', element: 'submit'}}
              />
            </form>
          </nyc.Col>
          <nyc.Col md={4}>
            <div>
              <h5 className="u-no-spacing-top">Organization Avatar</h5>
              <Avatar
                className="u-small-spacing-bottom"
                size={AvatarSize.XLARGE}
                shape={AvatarShape.SQUARE}
                entity={{
                  avatar_url: this.props.organization.avatar_url,
                  handle: this.props.organization.handle,
                  name: this.props.organization.name,
                }}
              />
              <input
                className="u-small-spacing-bottom"
                type="file"
                onChange={this._handleAvatarChange}
                disabled={disableInputs}
                {...getTestProps({
                  component: 'OrganizationSettingsTab',
                  element: 'avatarInput',
                })}
              />
              {!hasDefaultAvatar(this.props.organization) && this.props.isCurrentUserAdmin && (
                <a onClick={this._handleRemoveAvatar}>Remove avatar</a>
              )}
            </div>
            <hr />
            <div>
              <h5>Security and Backup</h5>
              <a
                onClick={() => {
                  ExportModal.open();
                }}
              >
                Export Data
              </a>
            </div>
            {!BenchlingConfig.PRIVATE_DEPLOY && (
              <div>
                <hr />
                <div>
                  Visit{' '}
                  <a href="/enterprise" target="_blank" rel="noopener noreferrer">
                    Enterprise plans
                  </a>
                  {' to see more features that Benchling can offer you.'}
                </div>
              </div>
            )}
            {this.props.isCurrentUserAdmin && (
              <div>
                <hr />
                <div>
                  <a className="organizationSettings-delete" onClick={this._handleTombstone}>
                    Delete Organization
                  </a>
                </div>
              </div>
            )}
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export default OrganizationSettingsTab;
